/** @file Delete confirmation: the selection, what goes with it (counts per kind), then one delete batch. */
import { useMemo } from 'react';
import type { Doc } from '@pose2d/format/types';
import type { Translate } from '@pose2d/i18n/types';
import { Button } from '../ui-kit/Button';
import { Dialog } from '../ui-kit/Dialog';
import { deletePlan, type DeleteTarget } from './deletePlan';
import { entityName } from './entityName';
import { submitCalls, type SubmitResult } from './submitCalls';

/** Props of DeleteDialog; `targets` null = closed. */
export interface DeleteDialogProps {
  doc: Doc;
  targets: readonly DeleteTarget[] | null;
  onClose: () => void;
  /** Receives the dispatch result after Delete. */
  onDone: (result: SubmitResult) => void;
  t: Translate;
}

/** Delete closes at once; the outcome arrives through `onDone`. Targets the commands would refuse are left out, and a list with none left opens no dialog. */
export function DeleteDialog({ doc, targets, onClose, onDone, t }: DeleteDialogProps) {
  const plan = useMemo(() => {
    const planned = targets === null || targets.length === 0 ? null : deletePlan(doc, targets);
    return planned === null || planned.calls.length === 0 ? null : planned;
  }, [doc, targets]);
  const first = plan?.planned[0];
  const title = plan !== null && plan.planned.length > 1 ? t('forms.delete.many', { n: plan.planned.length }) : t('forms.delete.one', { name: first ? entityName(doc, first.kind, first.id) : '' });
  const confirm = (): void => { if (plan !== null) void submitCalls(plan.calls).then(onDone); onClose(); };
  const footer = <><Button label={t('common.cancel')} onClick={onClose} /><Button label={t('common.delete')} danger onClick={confirm} /></>;
  return (
    <Dialog open={plan !== null} title={title} closeLabel={t('common.cancel')} onClose={onClose} footer={footer}>
      {plan !== null && plan.alsoRemoved.length > 0 && <p>{t('forms.delete.alsoRemoves')}</p>}
      <ul className="p2-form-list">{plan?.alsoRemoved.map((r) => <li key={r.kind}>{t(`forms.kinds.${r.kind}`, { n: r.count })}</li>)}</ul>
    </Dialog>
  );
}
